import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { api, ApiError, session } from "../api";
import { Banner } from "../components/ui";
import { IconCheck } from "../components/icons";
import { AuthStage } from "../components/AuthStage";

type Step = "password" | "totp-setup";

/** Live password requirements. Showing them as a checklist that ticks off as
 *  you type beats a single error after submitting: the rule is visible before
 *  it is broken, which is the difference between guidance and a scolding. */
function rules(pw: string) {
  return [
    { ok: pw.length >= 12, text: "At least 12 characters" },
    { ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw), text: "Upper and lower case" },
    { ok: /\d/.test(pw), text: "At least one number" },
  ];
}

export function Enroll() {
  const state = (useLocation().state as { mustChangePassword?: boolean; mustSetupTotp?: boolean } | null) ?? {};
  const [step, setStep] = useState<Step>(state.mustChangePassword ? "password" : "totp-setup");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [secret, setSecret] = useState("");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [code, setCode] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  const checks = useMemo(() => rules(next), [next]);
  const strong = checks.every((c) => c.ok);
  const matches = next.length > 0 && next === confirm;
  const totp = code.join("");

  useEffect(() => {
    if (!session.token) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    if (step === "totp-setup" && !secret) {
      api
        .totpSetup()
        .then((r) => {
          setSecret(r.secret);
          setOtpauthUrl(r.otpauth_url);
        })
        .catch(() => setError("Could not start enrolment. Try reloading the page."));
    }
  }, [step, secret]);

  useEffect(() => {
    if (otpauthUrl && canvasRef.current) {
      void QRCode.toCanvas(canvasRef.current, otpauthUrl, { width: 208, margin: 1 });
    }
  }, [otpauthUrl]);

  function setDigit(i: number, raw: string) {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      setCode((c) => c.map((v, j) => (j === i ? "" : v)));
      return;
    }
    setCode((c) => {
      const arr = [...c];
      for (let k = 0; k < digits.length && i + k < 6; k++) arr[i + k] = digits[k]!;
      return arr;
    });
    boxes.current[Math.min(5, i + digits.length)]?.focus();
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setStep("totp-setup");
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "WEAK_PASSWORD"
          ? "That password was rejected. It must be at least 12 characters."
          : "The temporary password is incorrect.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.totpActivate(totp);
      session.clear();
      navigate("/login", {
        state: { notice: "Two-factor is on. Sign in with your password and a code from your app." },
      });
    } catch {
      setCode(Array(6).fill(""));
      setError("That code was not accepted. Codes expire every 30 seconds.");
      boxes.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  }

  if (step === "password") {
    return (
      <div className="auth">
        <AuthStage />
        <div className="auth-panel">
        <form className="auth-form" onSubmit={submitPassword}>
          <h1>Choose a password</h1>
          <div className="sub">Step 1 of 2. Your temporary password must be replaced.</div>

          <div className="field">
            <label htmlFor="current">Temporary password</label>
            <input id="current" type="password" autoComplete="current-password" value={current}
                   onChange={(e) => setCurrent(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <label htmlFor="next">New password</label>
            <input id="next" type="password" autoComplete="new-password" value={next}
                   onChange={(e) => setNext(e.target.value)} required />
            <div style={{ marginTop: 10, display: "grid", gap: 5 }}>
              {checks.map((c) => (
                <div key={c.text} className="t-sm" style={{ display: "flex", alignItems: "center", gap: 7, color: c.ok ? "var(--gain)" : "var(--text-3)" }}>
                  <IconCheck size={13} />
                  {c.text}
                </div>
              ))}
            </div>
          </div>
          <div className="field">
            <label htmlFor="confirm">Confirm password</label>
            <input id="confirm" type="password" autoComplete="new-password" value={confirm}
                   onChange={(e) => setConfirm(e.target.value)} required />
            {confirm.length > 0 && !matches && <div className="field-hint loss">Passwords do not match.</div>}
          </div>

          {error && <Banner tone="error">{error}</Banner>}

          <button className="btn btn-primary btn-lg btn-block" style={{ marginTop: 20 }}
                  disabled={busy || !strong || !matches}>
            {busy ? "Saving" : "Continue"}
          </button>
        </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <AuthStage />
      <div className="auth-panel">
      <form className="auth-form" onSubmit={submitCode}>
        <h1>Set up two-factor</h1>
        <div className="sub">
          {state.mustChangePassword ? "Step 2 of 2. S" : "S"}can with 1Password, Google Authenticator or any TOTP app.
        </div>

        {otpauthUrl
          ? <div className="qr-box"><canvas ref={canvasRef} /></div>
          : <div className="skel" style={{ height: 232, borderRadius: 10, margin: "16px 0" }} />}

        {secret && (
          <details style={{ marginBottom: 20 }}>
            <summary className="t-sm dim" style={{ cursor: "pointer" }}>Can't scan? Enter this key instead</summary>
            <div className="secret-box" style={{ marginTop: 8 }}>{secret}</div>
          </details>
        )}

        <div className="field">
          <label>6-digit code</label>
          <div className="otp" role="group" aria-label="Authenticator code">
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => { boxes.current[i] = el; }}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={6}
                value={d}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !code[i] && i > 0) boxes.current[i - 1]?.focus();
                }}
              />
            ))}
          </div>
        </div>

        {error && <Banner tone="error">{error}</Banner>}

        <button className="btn btn-primary btn-lg btn-block" style={{ marginTop: 20 }}
                disabled={busy || totp.length !== 6}>
          {busy ? "Activating" : "Activate"}
        </button>
      </form>
      </div>
    </div>
  );
}
